import { useReportProductManifestProps } from '@euler/app/flows/report/functions/useReportProductManifestProps';
import { ProductManifestProps } from '@euler/app/flows/report/types';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { FaultInspectionItem } from '@euler/model/FaultInspectionItem';
import { TaskProductManifestItemOrigin } from '@euler/model/enum';
import { memo, useCallback } from 'react';
import { ScrollViewProps } from 'react-native';
import { ReportContentWrapper } from '../components/ReportContent';
import {
  ReportScreenProps,
  useReportContext,
} from '../components/ReportContext';
import { ConstructionReportView } from '../components/construction/ConstructionReportView';
import { ConstructionReportViewForIpad } from '../components/construction/ConstructionReportView.iPad';

type Props = {
  productManifest?: ProductManifestProps;
  targetJobId?: number;
  faultLikeSubject?: FaultInspectionItem;
};

const Content = memo(
  ({
    productManifest,
    targetJobId,
    faultLikeSubject,
    ...scrollViewProps
  }: ScrollViewProps & Props) => {
    const { report } = useReportContext();
    const isMobile = useIsMobileLayout();
    if (isMobile) {
      return (
        <ConstructionReportView
          report={report}
          productManifest={productManifest}
          targetJobId={targetJobId}
          faultLikeSubject={faultLikeSubject}
          {...scrollViewProps}
        />
      );
    }
    return (
      <ConstructionReportViewForIpad
        productManifest={productManifest}
        report={report}
        targetJobId={targetJobId}
        faultLikeSubject={faultLikeSubject}
      />
    );
  },
);

export const ConstructionReportScreen = wrapNavigatorScreen(
  ({
    title,
    productManifest: _productManifest,
    targetJobId,
    faultLikeSubject,
    ...props
  }: ReportScreenProps & Props) => {
    const productManifest = useReportProductManifestProps({
      productManifest: _productManifest,
      origin: { type: TaskProductManifestItemOrigin.ConstructionPreview },
    });

    const { rpx } = useResponsiveContext();
    const isMobile = useIsMobileLayout();
    const renderScrollView = useCallback(
      (scrollViewProps: ScrollViewProps) => {
        return (
          <Content
            targetJobId={targetJobId}
            productManifest={productManifest}
            faultLikeSubject={faultLikeSubject}
            {...scrollViewProps}
          />
        );
      },
      [faultLikeSubject, productManifest, targetJobId],
    );

    return (
      <ReportContentWrapper
        tab="construction"
        title={title ?? '施工报告'}
        {...props}
        renderScrollView={renderScrollView}
        shouldUseLandscapeLayout={!isMobile}
        ipadFooterStyle={{ paddingTop: rpx(15), paddingBottom: rpx(15) }}
      >
        <Content
          targetJobId={targetJobId}
          productManifest={productManifest}
          faultLikeSubject={faultLikeSubject}
        />
      </ReportContentWrapper>
    );
  },
  ({ route }) => ({
    headerShown: false,
    title: route.params.faultLikeSubject
      ? `${route.params.faultLikeSubject.title}-施工反馈`
      : '施工报告',
  }),
);
